/**
 * Public Guest Results Page — không cần đăng nhập.
 *
 * YC13 (2026-05-23): áp dụng template Landing — TopBar + Footer chung qua
 * `<PublicShell>`, hero section ICFGL navy/pink, eyebrow + section header
 * thống nhất, card grid có hover state.
 *
 * Spec R1→R2 transition Bước 5:
 *   "Báo cáo công khai chỉ liệt kê 4 đội đi tiếp theo thứ tự TÊN ĐỘI
 *    (không theo thứ hạng) để giảm áp lực so sánh"
 */
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Button,
  Grid,
} from '@mui/material'
import {
  CheckCircle,
  EmojiEvents,
  ArrowBack,
  AccountBalance,
  Gavel,
  ArrowForward,
} from '@mui/icons-material'
import {
  useGetPublicResultsCasesQuery,
  useGetPublicLatestResultsQuery,
  useGetPublicVerdictsQuery,
} from '../store/api/reportsApi'
import PublicShell from '../components/PublicShell'
import { brand } from '../theme/brand'

const NAVY = brand.navy
const PINK = brand.hotPink
const GOLD = '#C9A24E'
const BG_SOFT = '#FAFBFD'
const HAIRLINE = 'rgba(20,78,140,0.12)'
const TEXT_DIM = '#5E6B7E'

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: '0.76rem',
        letterSpacing: 3,
        color: PINK,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
  )
}

function PageHero({
  eyebrow,
  title,
  subtitle,
  icon,
}: {
  eyebrow: string
  title: string
  subtitle?: string
  icon?: React.ReactNode
}) {
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${PINK} 100%)`,
        color: 'common.white',
        py: { xs: 6, sm: 9 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 12% 40%, rgba(255,255,255,0.18) 0%, transparent 45%), radial-gradient(circle at 88% 70%, rgba(255,255,255,0.12) 0%, transparent 40%)',
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          {icon && (
            <Box
              sx={{
                width: 78,
                height: 78,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.16)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: GOLD,
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            sx={{
              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: '0.78rem',
              letterSpacing: 4,
              fontWeight: 700,
              textTransform: 'uppercase',
              opacity: 0.85,
            }}
          >
            {eyebrow}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.7rem', sm: '2.3rem' },
              lineHeight: 1.15,
              maxWidth: 880,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                opacity: 0.92,
                maxWidth: 720,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
      </Container>
    </Box>
  )
}

function CaseListLanding() {
  const { data, isLoading, error } = useGetPublicResultsCasesQuery()

  return (
    <>
      <PageHero
        eyebrow="Trang công bố kết quả"
        title="Kết quả Cuộc thi Phiên toà Giả định"
        subtitle="Gender Justice Moot Court — danh sách cuộc thi đã có kết quả công khai."
        icon={<EmojiEvents sx={{ fontSize: 44 }} />}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 7 } }}>
        <Stack spacing={1.2} mb={4}>
          <Eyebrow>Cuộc thi</Eyebrow>
          <Typography variant="h5" fontWeight={700} sx={{ color: NAVY }}>
            Chọn cuộc thi để xem kết quả
          </Typography>
          <Typography sx={{ color: TEXT_DIM, fontSize: '0.95rem', maxWidth: 720 }}>
            Mỗi cuộc thi liệt kê danh sách đội vào vòng tiếp theo (theo tên đội) và
            bản án Chung kết (nếu đã có).
          </Typography>
        </Stack>

        {isLoading && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Hiện chưa có kết quả công bố nào.
          </Alert>
        )}
        {data && data.length === 0 && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Hiện chưa có cuộc thi nào được công bố kết quả.
          </Alert>
        )}

        <Grid container spacing={2.5}>
          {data?.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.case_id}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  '&:hover': {
                    borderColor: NAVY,
                    boxShadow: '0 10px 32px rgba(20,78,140,0.12)',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${NAVY} 0%, ${PINK} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'common.white',
                  }}
                >
                  <AccountBalance fontSize="small" />
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: NAVY, lineHeight: 1.3 }}>
                  {c.title}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  component={RouterLink}
                  to={`/public/results/${c.case_id}`}
                  variant="contained"
                  endIcon={<ArrowForward />}
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: NAVY,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#0E3970' },
                  }}
                >
                  Xem kết quả
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  )
}

function CaseResultsView({ caseId }: { caseId: string }) {
  const { data, isLoading, error } = useGetPublicLatestResultsQuery(caseId)
  const { data: verdicts = [] } = useGetPublicVerdictsQuery(caseId)

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 9, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    )
  }
  if (error || !data) {
    return (
      <Container maxWidth="md" sx={{ py: 7 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Chưa có kết quả công bố cho cuộc thi này.
        </Alert>
        <Button
          component={RouterLink}
          to="/public/results"
          startIcon={<ArrowBack />}
          sx={{ mt: 2, textTransform: 'none' }}
        >
          Quay lại
        </Button>
      </Container>
    )
  }

  const publishedDate = data.published_at
    ? new Date(data.published_at).toLocaleDateString('vi-VN')
    : '—'

  return (
    <>
      <PageHero
        eyebrow={data.round?.round_name || 'Báo cáo công khai'}
        title={data.case_title}
        subtitle={`Công bố ngày ${publishedDate}`}
        icon={<EmojiEvents sx={{ fontSize: 44 }} />}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 7 } }}>
        <Button
          component={RouterLink}
          to="/public/results"
          startIcon={<ArrowBack />}
          sx={{ mb: 3, textTransform: 'none', color: NAVY }}
        >
          Tất cả cuộc thi
        </Button>

        {/* Stats */}
        <Grid container spacing={2.5} mb={5}>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${HAIRLINE}`,
                borderRadius: 2,
                background: BG_SOFT,
                borderTop: `4px solid ${NAVY}`,
                textAlign: 'center',
              }}
            >
              <Typography variant="h2" fontWeight={800} sx={{ color: NAVY, lineHeight: 1 }}>
                {data.total_teams}
              </Typography>
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.9rem', mt: 1 }}>
                Tổng số đội tham gia
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${HAIRLINE}`,
                borderRadius: 2,
                background: BG_SOFT,
                borderTop: `4px solid ${GOLD}`,
                textAlign: 'center',
              }}
            >
              <Typography variant="h2" fontWeight={800} sx={{ color: GOLD, lineHeight: 1 }}>
                {data.advancing_count}
              </Typography>
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.9rem', mt: 1 }}>
                Đội vào vòng tiếp theo
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Advancing teams */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Stack spacing={1.2} mb={2.5}>
            <Eyebrow>Đội đi tiếp</Eyebrow>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CheckCircle sx={{ color: GOLD }} />
              <Typography variant="h5" fontWeight={700} sx={{ color: NAVY }}>
                Danh sách đội vào vòng tiếp theo
              </Typography>
            </Stack>
            <Typography sx={{ color: TEXT_DIM, fontSize: '0.85rem' }}>
              (Thứ tự công bố theo tên đội, không theo thứ hạng — Điều 10 Thể lệ)
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2.5 }} />

          <Stack spacing={1.5}>
            {data.advancing_teams.map((team, idx) => (
              <Paper
                key={`${team.team_name}_${idx}`}
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: BG_SOFT,
                  borderLeft: `4px solid ${GOLD}`,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <CheckCircle sx={{ color: GOLD }} />
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: NAVY }}>
                    {team.team_name}
                  </Typography>
                  <Typography sx={{ color: TEXT_DIM, fontSize: '0.88rem' }}>
                    {team.university}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>

          {data.note && (
            <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
              {data.note}
            </Alert>
          )}
        </Paper>

        {/* R3 — Bản án Chung kết */}
        {verdicts.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 4 },
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 2,
              background: `linear-gradient(135deg, rgba(20,78,140,0.04) 0%, transparent 100%)`,
            }}
          >
            <Stack spacing={1.2} mb={3}>
              <Eyebrow>Bản án Chung kết</Eyebrow>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Gavel sx={{ color: NAVY }} />
                <Typography variant="h5" fontWeight={700} sx={{ color: NAVY }}>
                  Bản án đã công bố
                </Typography>
              </Stack>
            </Stack>

            <Stack spacing={2}>
              {verdicts.map((v) => (
                <Paper
                  key={v.courtroom_id}
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'background.paper',
                    border: `1px solid ${HAIRLINE}`,
                    borderLeft: `4px solid ${NAVY}`,
                    borderRadius: 1.5,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap">
                    {v.round_name && (
                      <Chip
                        size="small"
                        label={v.round_name}
                        sx={{ bgcolor: NAVY, color: 'common.white', fontWeight: 600 }}
                      />
                    )}
                    {v.winner_team && (
                      <Chip
                        size="small"
                        icon={<EmojiEvents sx={{ color: `${GOLD} !important` }} />}
                        label={`Đội thắng: ${v.winner_team.team_name}`}
                        sx={{ borderColor: GOLD, color: GOLD }}
                        variant="outlined"
                      />
                    )}
                    {v.published_at && (
                      <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem' }}>
                        Công bố {new Date(v.published_at).toLocaleDateString('vi-VN')}
                      </Typography>
                    )}
                  </Stack>
                  {v.verdict_summary && (
                    <Typography sx={{ fontWeight: 600, mb: 1, color: NAVY }}>
                      {v.verdict_summary}
                    </Typography>
                  )}
                  {v.verdict_legal_basis && (
                    <Box mt={1.5}>
                      <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem' }}>
                        Căn cứ pháp lý:
                      </Typography>
                      <Typography sx={{ fontSize: '0.92rem' }}>{v.verdict_legal_basis}</Typography>
                    </Box>
                  )}
                  {v.verdict_penalty && (
                    <Box mt={1.5}>
                      <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem' }}>
                        Hình phạt / yêu cầu:
                      </Typography>
                      <Typography sx={{ fontSize: '0.92rem' }}>{v.verdict_penalty}</Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          </Paper>
        )}
      </Container>
    </>
  )
}

export default function PublicResultsPage() {
  const { caseId } = useParams<{ caseId?: string }>()
  return (
    <PublicShell>
      {caseId ? <CaseResultsView caseId={caseId} /> : <CaseListLanding />}
    </PublicShell>
  )
}
